import { validateCustomRegionsSearch } from '@products/features/custom-regions-filters';
import { validateProductsSearch } from '@products/features/products-filters';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { PRODUCT_ACTIVITY_FILTER_OPTIONS } from '@travel-planner/shared/schemas';
import { protectedRoute, redirect } from './_root';

export const productsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'products',
  component: lazyRouteComponent(() =>
    import('@products/pages/products-list').then((m) => ({
      default: m.ProductsListPage,
    }))
  ),
  validateSearch: validateProductsSearch,
});

export const productDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'products/$productId',
  component: lazyRouteComponent(() =>
    import('@products/pages/product-detail').then((m) => ({
      default: m.ProductDetailPage,
    }))
  ),
});

// ─── Product Edit (inline validateSearch + legacy tab redirects) ──

const PRODUCT_EDIT_TAB_IDS = [
  'supplier',
  'description',
  'location-time',
  'media',
  'pricing',
  'facilities',
  'card-details',
  'attachments',
  'subitems',
  'private-notes',
  'public-notes',
  'advisor-notes',
  'cms',
  'activity',
] as const;
type ProductEditTabId = (typeof PRODUCT_EDIT_TAB_IDS)[number];

// Activity Log action filter (XT-519 §10). Lives in the URL like every other
// list filter, so a filtered audit view is linkable. Scoped to the actions the
// tab actually offers, not the full event vocabulary: an action the Select has
// no option for would filter the list while the control still read "All
// activity", and the recipient of the link could not clear it.
type ProductActivityAction = Exclude<
  (typeof PRODUCT_ACTIVITY_FILTER_OPTIONS)[number]['value'],
  'all'
>;

type ProductEditSearch = {
  pane?: ProductEditTabId;
  activityAction?: ProductActivityAction;
};

const isProductEditTabId = (value: unknown): value is ProductEditTabId =>
  typeof value === 'string' &&
  (PRODUCT_EDIT_TAB_IDS as readonly string[]).includes(value);

const isProductActivityAction = (
  value: unknown
): value is ProductActivityAction =>
  typeof value === 'string' &&
  value !== 'all' &&
  PRODUCT_ACTIVITY_FILTER_OPTIONS.some((option) => option.value === value);

const validateProductEditSearch = (
  input: Record<string, unknown>
): ProductEditSearch => {
  const search: ProductEditSearch = {};
  if (isProductEditTabId(input.pane)) search.pane = input.pane;
  // The action filter only means something on the Activity Log tab; dropping
  // it elsewhere keeps a stale filter from following the user between tabs.
  if (
    search.pane === 'activity' &&
    isProductActivityAction(input.activityAction)
  ) {
    search.activityAction = input.activityAction;
  }
  return search;
};

export const productEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'products/$productId/edit',
  component: lazyRouteComponent(() =>
    import('@products/pages/product-edit').then((m) => ({
      default: m.ProductEditPage,
    }))
  ),
  validateSearch: validateProductEditSearch,
});

// Legacy per-tab routes (`/edit/supplier`, `/edit/cms`, etc.) redirect to
// the parent route + `?pane=<id>` so links from the product-detail page,
// subitem-row "Open" buttons, Slack/email bookmarks, and the like keep
// working after the unified-Save flip.
export const productEditLegacyTabRoutes = PRODUCT_EDIT_TAB_IDS.map((tabId) =>
  createRoute({
    getParentRoute: () => productEditRoute,
    path: tabId,
    beforeLoad: ({ params }) => {
      throw redirect({
        to: '/products/$productId/edit',
        params: { productId: params.productId },
        search: tabId === 'supplier' ? {} : { pane: tabId },
      });
    },
  })
);

export const autoTranslateCatalogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'auto-translate-catalog',
  component: lazyRouteComponent(() =>
    import('@products/pages/auto-translate-catalog').then((m) => ({
      default: m.AutoTranslateCatalogPage,
    }))
  ),
});

export const customRegionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'custom-regions',
  component: lazyRouteComponent(() =>
    import('@products/pages/custom-regions').then((m) => ({
      default: m.CustomRegionsPage,
    }))
  ),
  validateSearch: validateCustomRegionsSearch,
});
